import { useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { PresetOption } from "./types";
import { buttonSizes } from "./constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../../ui/dropdown-menu";
import {cn} from "@gettactic/helpers";

interface PresetDropdownProps {
  presets: PresetOption[];
  onSelect: (value: number) => void;
  size?: keyof typeof buttonSizes;
}

export function PresetDropdown({
  presets,
  onSelect,
  size = "default",
}: PresetDropdownProps) {
  const [search, setSearch] = useState("");

  const groupedPresets = presets.reduce(
    (acc, preset) => {
      if (!acc[preset.category]) {
        acc[preset.category] = [];
      }
      acc[preset.category].push(preset);
      return acc;
    },
    {} as Record<string, PresetOption[]>,
  );

  const filteredGroups = Object.entries(groupedPresets).reduce(
    (acc, [category, items]) => {
      const filtered = items.filter(
        (preset) =>
          preset.label.toLowerCase().includes(search.toLowerCase()) ||
          category.toLowerCase().includes(search.toLowerCase()),
      );
      if (filtered.length > 0) {
        acc[category] = filtered;
      }
      return acc;
    },
    {} as Record<string, PresetOption[]>,
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(buttonSizes[size], "px-0 absolute right-0 top-0")}
          aria-label="Open size presets"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <div className="p-2">
          <div className="flex items-center gap-2 px-2 pb-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              placeholder="Search presets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
            />
          </div>
          {Object.entries(filteredGroups).map(
            ([category, categoryPresets], index) => (
              <div key={category}>
                {index > 0 && <DropdownMenuSeparator />}
                <DropdownMenuGroup>
                  <DropdownMenuLabel>{category}</DropdownMenuLabel>
                  {categoryPresets.map((preset) => (
                    <DropdownMenuItem
                      key={`${category}-${preset.label}`}
                      onClick={() => onSelect(preset.value)}
                    >
                      {preset.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </div>
            ),
          )}
          {Object.keys(filteredGroups).length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-2">
              No presets found
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
